import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import { t } from 'i18next';
import { FormApi } from 'final-form';
import { Button } from 'ncoded-component-library';
import { Form } from 'react-final-form';
import SideDrawer from '../../../SideDrawer';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import { Question } from 'router/subrouters/DataRoom/models/QAndA';
import AnswerRichTextEditor from '../../../AnswerRichTextEditor';
import { QuestionPreview } from '../../../QuestionPreview';
import RejectionReasonBanner from '../../../RejectionReasonBanner';
import useCallbackRef from 'hooks/useCallbackRef';
import useGetFollowUpQuestion from '../../../QAndABuyerContainer/hooks/useGetFollowUpQuestion';
import { PreviewSideDrawerRef } from '../../../PreviewQuestionDrawer/PreviewQuestionDrawer.component';
import arrayMutators from 'final-form-arrays';

import './AnswerQuestionDrawer.styles.scss';

type AnswerQuestionDrawerProps = {
  question: Question;
  title: string;
  onSubmit: (answer: string) => void;
  onClose?: () => void;
  onQuestionsUpdate?: () => void;
  updateQuestion: (question: Partial<Question>) => void;
};

type AnswerQuestionDrawerForm = {
  dataRoomAnswer?: string;
};

const AnswerQuestionDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  AnswerQuestionDrawerProps
> = (
  { question, title, onSubmit, onClose, onQuestionsUpdate, updateQuestion },
  ref,
) => {
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const [drawer, drawerRef] = useCallbackRef<PreviewSideDrawerRef>();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { isLoading, displayedQuestions } = useGetFollowUpQuestion(
    question,
    drawer?.open,
  );

  const initialValues = {
    dataRoomAnswer: question?.dataRoomAnswer?.content,
  } as AnswerQuestionDrawerForm;

  const onRemoveAnswerFile = useCallback(
    (fileId: string) => {
      const newQ = {
        ...question,
        dataRoomAnswer: {
          ...question?.dataRoomAnswer,
          files: question?.dataRoomAnswer?.files?.filter(
            ({ _id }) => _id !== fileId,
          ),
        },
      };
      updateQuestion(newQ);
    },
    [question, updateQuestion],
  );

  const onQuestionManage = useCallback(
    async (
      values: AnswerQuestionDrawerForm,
      formApi: FormApi<
        AnswerQuestionDrawerForm,
        Partial<AnswerQuestionDrawerForm>
      >,
    ) => {
      try {
        await onSubmit(values.dataRoomAnswer);
        onClose?.();
        onQuestionsUpdate?.();
        popSuccess(t('answerSubmitted'));
        formApi.restart();
      } catch (e) {
        popServerError(t('qaError'));
      }
    },
    [onClose, onQuestionsUpdate, onSubmit, popServerError, popSuccess],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      className="qa-answer-question"
      closeAction={onClose}
      isLoading={isLoading}
    >
      <p className="drawer-title">{title}</p>

      {drawer?.open && (
        <Form
          mutators={{ ...arrayMutators }}
          initialValues={initialValues}
          onSubmit={onQuestionManage}
          render={({ handleSubmit, submitting, invalid }) => (
            <form onSubmit={handleSubmit} className="qa-answer-question__form">
              <QuestionPreview
                question={question}
                displayedQuestions={displayedQuestions}
                updateQuestion={updateQuestion}
              />

              <RejectionReasonBanner question={question} />

              <AnswerRichTextEditor
                label={t('provideAnswer')}
                richTextEditorName="dataRoomAnswer"
                answerId={question?.dataRoomAnswer?._id}
                initialFiles={question?.dataRoomAnswer?.files}
                onRemoveAnswerFile={onRemoveAnswerFile}
              />

              <Button type="submit" disabled={submitting || invalid}>
                {t('submitAnswer')}
              </Button>
            </form>
          )}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(AnswerQuestionDrawer);
